import { Skeleton } from 'antd'
import { HttpRequestHeader } from 'antd/lib/upload/interface'
import { LDFlagSet } from 'launchdarkly-js-client-sdk'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { PropsWithChildren, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'

import { FullPageLoad } from '../App'
import { useAuth } from '../authentication/AuthContext'
import { AccountListFilters } from '../components/dashboard/panels/AccountsPanel'
import { Features, Permissions, UnknownObject } from '../utils/data.types'

import {
  Account,
  AccountsListItem,
  AccountTotalBalance,
  AddNewRolePayload,
  AddRecipient,
  AdjustmentRes,
  AdjustmentReturn,
  Admin,
  ApiContextType,
  CoreTransaction,
  CreateAchPayment,
  CreateBookPayment,
  CreatedPayment,
  CreateWirePayment,
  DescriptionsReasonCodes,
  EditRecipient,
  ExportDetails,
  LedgerBalance,
  LedgerProgram,
  NewAdjustment,
  OfacDetails,
  OfacHit,
  Paged,
  PaymentReceipt,
  Permission,
  RCMatches,
  Recipient,
  RecipientDetails,
  RecipientsListElement,
  ReportsResultType,
  reportTypes,
  Response,
  ResponseJSON,
  Return,
  ReturnDetails,
  Role,
  ScheduledTransaction,
  Single,
  Timezone,
  TransactionClient,
  TransactionHistoryDetails,
  TransactionHistoryRes,
  TransactionProgram,
  TransactionSummary,
  UpdateRolePayload,
  VictorClient,
} from './api.types'

const JSON_CONTENT = { 'Content-Type': 'application/json' }

const ApiContext = React.createContext<ApiContextType | undefined>(undefined)

export { ApiContext }

const UNAUTHORIZED = 'Unauthorized'
let refreshing = false
declare global {
  let platformMetadata: PlatformMetadata
}

async function withRetry<T extends Response>(
  request: () => Promise<globalThis.Response>,
  refreshFn: () => Promise<boolean>
): Promise<T> {
  let response = await request()

  if (response.status === 401 && !refreshing) {
    refreshing = true
    try {
      if (await refreshFn()) {
        response = await request()
      }
    } finally {
      refreshing = false
    }
  } else if (refreshing) {
    while (refreshing) {
      await new Promise((res) => {
        setTimeout(res, 100)
      })
    }

    response = await request()
  }

  if (response.status === 401) {
    throw new Error(UNAUTHORIZED)
  }

  if (
    !response.headers.get('Content-Type')?.startsWith('application/json') &&
    (response.headers.get('Content-Type')?.startsWith('text') ||
      response.headers.get('Content-Type')?.startsWith('image') ||
      response.headers.get('Content-Type')?.startsWith('application/'))
  ) {
    if (response.status < 200 || response.status >= 400) {
      throw new Error('Unsuccessful response')
    }

    return (await response?.blob()) as T
  } else if (response.headers.get('Content-Type') !== null) {
    let body

    try {
      body = (await response.json()) as ResponseJSON
    } catch (e) {
      console.error('unable to parse response as JSON', e)
      throw new Error('Unsuccessful response')
    }

    if (body.status !== 1) {
      throw body.error ?? new Error('Unsuccessful response')
    }

    return body as T
  } else {
    if (response.status < 200 || response.status >= 400) {
      throw new Error('Unsuccessful response')
    }
    return {} as T
  }
}

export default function ApiProvider(props: React.PropsWithChildren<unknown>): ReactElement {
  const { tokens, logout, refreshTokens } = useAuth()
  const [ready, setReady] = useState(false)

  const callService = useCallback(
    async <T extends Response>(url: URL, config?: RequestInit): Promise<T> => {
      try {
        return await withRetry(() => {
          const options = { ...config }
          options.headers = { ...options.headers, Authorization: `Token ${tokens().access}` }

          if (['put', 'post', 'patch'].includes(options.method?.toLowerCase() ?? '')) {
            options.headers = { ...options.headers, ...JSON_CONTENT }
          }

          return fetch(url.toString(), options)
        }, refreshTokens)
      } catch (e: any) {
        if (e.message === UNAUTHORIZED) {
          console.warn('session expired')
          await logout()
          throw new Error('Session expired')
        }

        throw e
      }
    },
    [tokens, logout, refreshTokens]
  )

  const createUrl = useCallback((path: string, filters?: UnknownObject, backOffice?: boolean) => {
    const url = new URL(
      `${backOffice ? process.env.REACT_APP_BACKOFFICE_HOST : process.env.REACT_APP_API_HOST}${
        path.startsWith('/') ? '' : '/'
      }${path}`
    )
    if (!filters) {
      return url
    }

    Object.keys(filters).forEach((key) => {
      if (filters[key] !== null && filters[key] !== undefined) {
        url.searchParams.append(key, `${filters[key]}`)
      }
    })

    return url
  }, [])

  const getAuthHeaders = useCallback((): HttpRequestHeader => {
    return {
      Authorization: `Token ${tokens().access}`,
    }
  }, [tokens])

  const getPermissions = useCallback(async () => {
    const { data } = await callService<Single<Permission[]>>(createUrl('/client/permissions'))
    return data
  }, [createUrl, callService])

  const getBookRouting = useCallback(async () => {
    const { data } = await callService<Single<string[]>>(createUrl('/transaction/book/external/config'))
    return data
  }, [createUrl, callService])

  const refreshExternalAccounts = useCallback(async () => {
    await callService<Single<void>>(createUrl('/account/refresh'))
  }, [createUrl, callService])

  const getCoreAccounts = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Paged<Account>>(createUrl('/account/core', filters))
      return data
    },
    [createUrl, callService]
  )

  const getAccounts = useCallback(
    async (filters: AccountListFilters = { index: 0, size: 20 }) => {
      const { data } = await callService<Paged<AccountsListItem>>(createUrl('/accounts', filters))
      return data
    },
    [createUrl, callService]
  )

  const getAccount = useCallback(
    async (accountUuid: string) => {
      const { data } = await callService<Single<Account>>(createUrl(`/account/${accountUuid}`))
      return data
    },
    [createUrl, callService]
  )

  const getReceivingAccountTypes = useCallback(
    async (paymentType: 'internal' | 'external', originatingAccountType: string) => {
      const { data } = await callService<Single<string[]>>(
        createUrl(
          `/accounts/receiving_account_types?payment_type=${paymentType}&originating_account_type=${originatingAccountType}`
        )
      )
      return data
    },
    [createUrl, callService]
  )

  const getCoreTransactions = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Paged<CoreTransaction>>(createUrl('/transaction/core', filters))
      return data
    },
    [createUrl, callService]
  )

  const getReturns = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Paged<Return>>(createUrl('/transaction/returns', filters))
      return data
    },
    [createUrl, callService]
  )

  const getReturn = useCallback(
    async (reference: string) => {
      const { data } = await callService<Single<ReturnDetails>>(createUrl(`/transaction/returns/${reference}`))
      return data
    },
    [createUrl, callService]
  )

  const getSummary = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Single<TransactionSummary[]>>(
        createUrl('/transaction/summary/transactions', filters)
      )
      return data
    },
    [createUrl, callService]
  )

  const getRecipient = useCallback(
    async (id: string) => {
      const { data } = await callService<Single<Recipient>>(createUrl(`/counterparty/${id}`))
      return data
    },
    [createUrl, callService]
  )

  const getRecipientByReference = useCallback(
    async (reference: string) => {
      const { data } = await callService<Single<RecipientDetails>>(createUrl(`/counterparties/${reference}`))
      return data
    },
    [createUrl, callService]
  )

  /**Returns list with basic information about recipients */
  const getRecipientsBasicList = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Paged<RecipientsListElement>>(createUrl('/counterparties', filters))
      return data
    },
    [createUrl, callService]
  )

  /**Returns list with more detail information about recipients */
  const getRecipients = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Paged<Recipient>>(createUrl('/counterparty', filters))
      return data
    },
    [createUrl, callService]
  )

  const getRecipientNames = useCallback(
    async (filters?: UnknownObject) => {
      const { data } = await callService<Single<string[]>>(createUrl('/counterparty/account-names', filters))
      return data
    },
    [createUrl, callService]
  )

  const addRecipient = useCallback(
    async (recipient: AddRecipient) => {
      const { data } = await callService<Single<Recipient>>(createUrl('/counterparty'), {
        method: 'POST',
        body: JSON.stringify(recipient),
      })

      return data
    },
    [createUrl, callService]
  )

  const updateRecipient = useCallback(
    async (recipient: EditRecipient) => {
      const { data } = await callService<Single<Recipient>>(createUrl('/counterparty'), {
        method: 'PUT',
        body: JSON.stringify(recipient),
      })

      return data
    },
    [createUrl, callService]
  )

  const getLedgers = useCallback(async () => {
    const { data } = await callService<Single<LedgerProgram[]>>(createUrl('/account/ledgering-program'))
    return data
  }, [createUrl, callService])

  const getLedgerAccounts = useCallback(
    async (ledgerId: bigint, filters: UnknownObject) => {
      const { data } = await callService<Paged<Account>>(
        createUrl(`/account/ledgering-program/${ledgerId}/accounts`, filters)
      )

      return data
    },
    [createUrl, callService]
  )

  const getLedgerBalance = useCallback(
    async (ledgerId: bigint) => {
      const { data } = await callService<Single<LedgerBalance>>(
        createUrl(`/account/ledgering-program/${ledgerId}/balance`)
      )

      return data
    },
    [createUrl, callService]
  )

  const createWirePayment = useCallback(
    async (createPaymentData: CreateWirePayment) => {
      const { data } = await callService<Single<PaymentReceipt>>(createUrl('/transaction/wire'), {
        method: 'POST',
        body: JSON.stringify(createPaymentData),
      })

      return data
    },
    [createUrl, callService]
  )

  const createAchPayment = useCallback(
    async (createPaymentData: CreateAchPayment) => {
      const { data } = await callService<Single<PaymentReceipt>>(createUrl('/transaction/ach'), {
        method: 'POST',
        body: JSON.stringify(createPaymentData),
      })

      return data
    },
    [createUrl, callService]
  )

  const createBookPayment = useCallback(
    async (createPaymentData: CreateBookPayment) => {
      const { data } = await callService<Single<CreatedPayment>>(
        createUrl(`/transaction/book/${createPaymentData.book_type}`),
        {
          method: 'POST',
          body: JSON.stringify(createPaymentData),
        }
      )

      return data
    },
    [createUrl, callService]
  )

  const sendPaymentOtp = useCallback(async () => {
    await callService<Single<CreatedPayment>>(createUrl('/transaction/generateOtp'))
  }, [createUrl, callService])

  const approveWire = useCallback(
    async (reference: string, pin: string) => {
      const { data } = await callService<Single<PaymentReceipt>>(createUrl(`/transaction/wire/approve/${reference}`), {
        method: 'POST',
        body: JSON.stringify({ pin }),
      })

      return data
    },
    [createUrl, callService]
  )

  const declineWire = useCallback(
    async (reference: string, pin: string) => {
      await callService<Single<void>>(createUrl(`/transaction/wire/decline/${reference}`), {
        method: 'POST',
        body: JSON.stringify({ pin }),
      })
    },
    [createUrl, callService]
  )

  const approveRecipient = useCallback(
    async (reference: string, pin: string) => {
      await callService<Single<CreatedPayment>>(createUrl(`/counterparty/approve/${reference}`), {
        method: 'PUT',
        body: JSON.stringify({ pin }),
      })
    },
    [createUrl, callService]
  )

  const denyRecipient = useCallback(
    async (reference: string, pin: string) => {
      await callService<Single<CreatedPayment>>(createUrl(`/counterparty/deny/${reference}`), {
        method: 'PUT',
        body: JSON.stringify({ pin }),
      })
    },
    [createUrl, callService]
  )

  const exportTransactions = useCallback(
    async (filters: UnknownObject, coreExport?) => {
      const response = await callService<Blob>(createUrl(`/transaction/export${coreExport ? 'Core' : ''}`, filters), {
        headers: { accept: 'text/csv' },
      })

      promptDownload(response, 'transactions-export.csv')
    },
    [createUrl, callService]
  )

  const exportLedger = useCallback(
    async (exportDetails: ExportDetails) => {
      let urlString = `/account/ledgering-program/${exportDetails.ledgeringProgramId}/export`
      if (exportDetails.virtualAccountId) {
        urlString += '/account'

        if (exportDetails.virtualAccountId !== 'all') {
          urlString += `/${exportDetails.virtualAccountId}`
        }
      }

      const { start_date, end_date } = exportDetails

      const response = await callService<Blob>(createUrl(urlString, { start_date, end_date }), {
        method: 'GET',
        headers: { accept: 'text/csv' },
      })

      promptDownload(
        response,
        `export-${exportDetails.ledgeringProgramId}
        ${exportDetails.virtualAccountId ? `-${exportDetails.virtualAccountId}` : ''}.csv`
      )
    },
    [createUrl, callService]
  )

  const getVictorClients = useCallback(async () => {
    const { data } = await callService<Single<VictorClient[]>>(createUrl('/bo/lookup/client', {}, true))
    return data ?? []
  }, [createUrl, callService])

  const getVictorClientAccounts = useCallback(
    async (clientUuid: string) => {
      const filters = { status: 1, size: 100 }
      const { data } = await callService<Paged<Account>>(createUrl(`/bo/account/${clientUuid}`, filters, true))
      return data ?? []
    },
    [createUrl, callService]
  )

  const createAdjustment = useCallback(
    async (account_number, amount, remarks, transaction_type) => {
      const { data } = await callService<Single<NewAdjustment>>(createUrl('bo/transaction/adjustment', {}, true), {
        method: 'POST',
        body: JSON.stringify({
          account_number,
          amount,
          remarks,
          transaction_type,
        }),
      })
      return data
    },
    [createUrl, callService]
  )

  const getOtp = useCallback(async () => {
    const { data } = await callService<Single<string>>(createUrl('/bo/property/otpConfig', {}, true))
    return data
  }, [callService, createUrl])

  const setOtp = useCallback(
    async (otpValue: string) => {
      const { data } = await callService<Single<string>>(createUrl('/bo/property/otpConfig', {}, true), {
        method: 'PUT',
        body: JSON.stringify({ value: otpValue }),
      })
      return data
    },
    [callService, createUrl]
  )

  const getTransactionsHistory = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Paged<TransactionHistoryRes>>(createUrl('/bo/transactions', filters, true))
      return data
    },
    [createUrl, callService]
  )

  const getTransactionsHistoryDetails = useCallback(
    async (thReference: string) => {
      const { data } = await callService<Single<TransactionHistoryDetails>>(
        createUrl(`/bo/transactions/${thReference}`, {}, true)
      )
      return data
    },
    [createUrl, callService]
  )

  const getTransactionsClients = useCallback(async () => {
    const filters = { index: 0, size: 2000 }
    const { data } = await callService<Paged<TransactionClient>>(createUrl('/bo/clients', filters, true))
    return data
  }, [createUrl, callService])

  const getAdjustments = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Paged<AdjustmentRes>>(createUrl('/bo/transaction/adjustment', filters, true))
      return data
    },
    [createUrl, callService]
  )

  const approveAdjustment = useCallback(
    async (adjustment_id: number, remark: string) => {
      const { data } = await callService<Single<AdjustmentReturn>>(
        createUrl('bo/transaction/adjustment/approve', {}, true),
        {
          method: 'POST',
          body: JSON.stringify({ adjustment_id: adjustment_id, remarks: remark }),
        }
      )
      return data
    },
    [createUrl, callService]
  )

  const declineAdjustment = useCallback(
    async (adjustment_id: number, remark: string) => {
      await callService<Single<void>>(createUrl('bo/transaction/adjustment/decline', {}, true), {
        method: 'POST',
        body: JSON.stringify({ adjustment_id: adjustment_id, remarks: remark }),
      })
    },
    [createUrl, callService]
  )

  const getReports = useCallback(
    async (filters: UnknownObject, report: reportTypes) => {
      const { data } = await callService<Paged<ReportsResultType>>(
        createUrl(`/bo/transaction/report/${report}`, filters, true)
      )
      return data
    },
    [createUrl, callService]
  )

  const getAdmins = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Paged<Admin>>(createUrl('/admin/user', filters, true))
      return data
    },
    [callService, createUrl]
  )

  const addAdmin = useCallback(
    async (admin: Admin) => {
      await callService<Single<Admin>>(createUrl('/admin/user', {}, true), {
        method: 'POST',
        body: JSON.stringify(admin),
      })
    },
    [callService, createUrl]
  )

  const updateAdmin = useCallback(
    async (admin: Admin) => {
      await callService<Single<Admin>>(createUrl('/admin/user', {}, true), {
        method: 'PUT',
        body: JSON.stringify(admin),
      })
    },
    [callService, createUrl]
  )

  const getAdminRoles = useCallback(async () => {
    const { data } = await callService<Single<Role[]>>(createUrl('/admin/lookup/roles', {}, true))
    return data
  }, [callService, createUrl])

  const setAdminStatus = useCallback(
    async (adminId: number, status: number) => {
      await callService<Single<Admin>>(createUrl('/admin/status', {}, true), {
        method: 'PUT',
        body: JSON.stringify({ login_id: adminId, status }),
      })
    },
    [callService, createUrl]
  )

  const getRolePermissions = useCallback(
    async (roleID: number) => {
      const { data } = await callService<Single<Permission[]>>(createUrl(`/admin/permissions/${roleID}`, {}, true))
      return data
    },
    [callService, createUrl]
  )

  const updateRole = useCallback(
    async (payload: UpdateRolePayload) => {
      return await callService<Single<string>>(createUrl('admin/role'), {
        method: 'PUT',
        body: JSON.stringify({ ...payload }),
      })
    },
    [callService, createUrl]
  )
  const addNewRole = useCallback(
    async (payload: AddNewRolePayload) => {
      return await callService<Single<string>>(createUrl('admin/role'), {
        method: 'POST',
        body: JSON.stringify({ ...payload }),
      })
    },
    [callService, createUrl]
  )

  const getAdminPermissions = useCallback(async () => {
    const { data } = await callService<Single<Permission[]>>(createUrl(`/admin/lookup/permissions`))
    return data
  }, [callService, createUrl])

  const getTimezones = useCallback(async () => {
    const { data } = await callService<Single<Timezone[]>>(createUrl('/bo/lookup/timezones', {}, true))
    return data
  }, [callService, createUrl])

  const getOfacHits = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Paged<OfacHit>>(createUrl('/bo/transaction/ofac', filters))
      return data
    },
    [createUrl, callService]
  )

  const getOfacHit = useCallback(
    async (reference: string) => {
      const { data } = await callService<Paged<OfacHit>>(createUrl('/bo/transaction/ofac', { reference }))
      return data.results[0]
    },
    [createUrl, callService]
  )

  const getOfacHitDetails = useCallback(
    async (detailsId: number) => {
      const { data } = await callService<Single<OfacDetails>>(createUrl(`/bo/transaction/ofac/${detailsId}`))
      return data
    },
    [createUrl, callService]
  )

  const getScheduledAndRecurringTransactions = useCallback(
    async (filters: UnknownObject) => {
      const { data } = await callService<Paged<ScheduledTransaction>>(
        createUrl('/bo/transaction/recurring', filters, true)
      )

      return data
    },
    [createUrl, callService]
  )

  const getReasonCodesMatches = useCallback(
    async (referenceId: string) => {
      const { data } = await callService<Single<RCMatches>>(createUrl(`/compliance/external/reference/${referenceId}`))
      return data
    },
    [createUrl, callService]
  )

  const approveOfacTransaction = useCallback(
    async (transactionId: string, remark: string) => {
      const { data } = await callService<any>(createUrl('/bo/transaction/ofac/approve', {}, true), {
        method: 'POST',
        body: JSON.stringify({ transaction_id: transactionId, remarks: remark }),
      })
      return data
    },
    [createUrl, callService]
  )

  const declineOfacTransaction = useCallback(
    async (transactionId: string, remark: string) => {
      const { data } = await callService<any>(createUrl('/bo/transaction/ofac/decline', {}, true), {
        method: 'POST',
        body: JSON.stringify({ transaction_id: transactionId, remarks: remark }),
      })
      return data
    },
    [createUrl, callService]
  )

  const suppressMatchTransaction = useCallback(
    async (referenceId: string, entityId: string) => {
      const { data } = await callService<any>(
        createUrl(`/compliance/external/notification/suppress/reference/${referenceId}?entityId=${entityId}`),
        {
          method: 'POST',
        }
      )
      return data
    },
    [createUrl, callService]
  )

  const getTransactionsProgram = useCallback(async () => {
    const { data } = await callService<Single<TransactionProgram[]>>(createUrl('/bo/program/lookup', {}, true))
    return data
  }, [createUrl, callService])

  const getAccountBalance = useCallback(async () => {
    const { data } = await callService<Single<AccountTotalBalance>>(createUrl('/accounts/total-balance', {}, false))
    return data
  }, [createUrl, callService])

  const getReasonCodeDescription = useCallback(async () => {
    const { data } = await callService<Single<DescriptionsReasonCodes>>(
      createUrl('/compliance/external/reasoncodes/ofac')
    )
    return data
  }, [createUrl, callService])

  const accountContextValue = useMemo(
    () => ({
      createUrl,
      callService,
      getAuthHeaders,
      getPermissions,
      getBookRouting,
      refreshExternalAccounts,
      getCoreAccounts,
      getAccounts,
      getReceivingAccountTypes,
      getAccount,
      getCoreTransactions,
      getReturns,
      getReturn,
      getSummary,
      getRecipients,
      getRecipientNames,
      getRecipientsBasicList,
      getRecipient,
      getRecipientByReference,
      addRecipient,
      updateRecipient,
      getLedgers,
      getLedgerAccounts,
      getLedgerBalance,
      createWirePayment,
      createAchPayment,
      createBookPayment,
      sendPaymentOtp,
      approveWire,
      declineWire,
      approveRecipient,
      denyRecipient,
      exportTransactions,
      exportLedger,
      getVictorClients,
      getAdmins,
      addAdmin,
      getOtp,
      setOtp,
      updateAdmin,
      getRolePermissions,
      updateRole,
      addNewRole,
      getAdminRoles,
      setAdminStatus,
      getAdminPermissions,
      getTimezones,
      getReports,
      getReasonCodesMatches,
      approveOfacTransaction,
      declineOfacTransaction,
      suppressMatchTransaction,
      getOfacHit,
      getOfacHits,
      getOfacHitDetails,
      getScheduledAndRecurringTransactions,
      getTransactionsHistory,
      getAdjustments,
      approveAdjustment,
      declineAdjustment,
      getTransactionsProgram,
      getTransactionsHistoryDetails,
      getTransactionsClients,
      getVictorClientAccounts,
      createAdjustment,
      getAccountBalance,
      getReasonCodeDescription,
    }),
    [
      createUrl,
      callService,
      getAuthHeaders,
      getPermissions,
      getBookRouting,
      refreshExternalAccounts,
      getCoreAccounts,
      getAccounts,
      getReceivingAccountTypes,
      getAccount,
      getCoreTransactions,
      getReturns,
      getReturn,
      getSummary,
      getRecipients,
      getRecipientNames,
      getRecipientsBasicList,
      getRecipient,
      getRecipientByReference,
      addRecipient,
      updateRecipient,
      getLedgers,
      getLedgerAccounts,
      getLedgerBalance,
      createWirePayment,
      createAchPayment,
      createBookPayment,
      sendPaymentOtp,
      approveWire,
      declineWire,
      approveRecipient,
      denyRecipient,
      exportTransactions,
      exportLedger,
      getVictorClients,
      getAdmins,
      addAdmin,
      getOtp,
      setOtp,
      updateAdmin,
      getRolePermissions,
      updateRole,
      addNewRole,
      getAdminRoles,
      setAdminStatus,
      getAdminPermissions,
      getTimezones,
      getReports,
      getReasonCodesMatches,
      approveOfacTransaction,
      declineOfacTransaction,
      suppressMatchTransaction,
      getOfacHit,
      getOfacHits,
      getOfacHitDetails,
      getScheduledAndRecurringTransactions,
      getTransactionsHistory,
      getAdjustments,
      approveAdjustment,
      declineAdjustment,
      getTransactionsProgram,
      getTransactionsHistoryDetails,
      getTransactionsClients,
      getVictorClientAccounts,
      createAdjustment,
      getAccountBalance,
      getReasonCodeDescription,
    ]
  )

  useEffect(() => {
    ;(async () => {
      try {
        const { data } = await callService<Single<TransactionMetadata>>(createUrl('/transaction/metadata'))
        global.platformMetadata = {
          transactions: data,
        }
      } catch (e) {
        console.error('unable to retrieve platform metadata', e)
        global.platformMetadata = {
          transactions: {
            types: [],
            status: [],
          },
        }
      } finally {
        setReady(true)
      }
    })()
  }, [createUrl, callService])

  if (!ready) {
    return <FullPageLoad />
  }

  return <ApiContext.Provider value={accountContextValue} {...props} />
}

export function useApi(): ApiContextType {
  const context = useContext(ApiContext)

  if (context === undefined) {
    throw new Error('useApi() must be used within a ApiContextProvider')
  }

  return context
}

export function useCachedPermissions() {
  const { activeUser } = useAuth()
  const { getPermissions } = useApi()
  const { data, error } = useSWR<Permission[], Error>(['permissionCheck', activeUser?.uuid], getPermissions, {
    dedupingInterval: parseInt(process.env.REACT_APP_REQUEST_CACHE_INTERVAL ?? '30000'),
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  })

  return {
    loading: data === undefined && !error, // api may return null for data
    permissions: data,
    error,
  }
}

export function hasAllPermissions(permissions: Permission[], required: Permissions[]): boolean {
  return required.every((r) => permissions.findIndex((p) => p.name === r.valueOf()) > -1)
}

export function hasAllFeatures(flags: LDFlagSet, features: Features[]): boolean {
  return features.every((f) => !!flags[f])
}

export function useRequirePermissions(...required: Permissions[]): boolean | undefined {
  const { permissions, error, loading } = useCachedPermissions()

  if (error) {
    console.error(error)
    return undefined
  }

  if (loading || !permissions) {
    return undefined
  }

  return hasAllPermissions(permissions, required)
}

export function Restricted({
  children,
  required,
  features,
  enabled = true,
  fallback,
  onRestricted,
}: PropsWithChildren<{
  required?: Permissions[]
  features?: Features[]
  enabled?: boolean
  fallback?: ReactElement
  onRestricted?: () => void
}>): ReactElement | null {
  const flags = useFlags()
  const { permissions, error, loading } = useCachedPermissions()

  if (loading || !permissions) {
    return <Skeleton active paragraph={false} />
  }

  if (error) {
    console.error(error)
    return fallback ? fallback : null
  }

  const requiredPermissions: Permissions[] = required || []
  const requiredFeatures: Features[] = features || []

  if (enabled && hasAllPermissions(permissions, requiredPermissions) && hasAllFeatures(flags, requiredFeatures)) {
    return <>{children}</>
  }

  if (onRestricted) {
    onRestricted()
  }

  return fallback ? fallback : null
}

export function promptDownload(blob: Blob, fileName: string) {
  const fileObjectUrl = URL.createObjectURL(blob)

  const download = document.createElement('a')
  download.href = fileObjectUrl
  download.target = '_blank'
  download.download = fileName
  download.style.display = 'none'

  document.body.appendChild(download)
  download.click()
  document.body.removeChild(download)
  URL.revokeObjectURL(fileObjectUrl)
}
